/**
 * 役种类型定义 + 判定上下文
 *
 * 役（yaku）= 和牌时必须满足的"牌型条件"，是计番的基础。
 * 没有役不能和牌（"无役不能和"）。
 */

import type { Mentsu, Decomposition } from '../agari.js';
import type { Counts } from '../tile.js';

// ════════════════════════════════════════════════════════════════
// 役种定义
// ════════════════════════════════════════════════════════════════

/** 役种类别 */
export type YakuCategory =
  | 'riichi' // 立直相关
  | 'tsumo' // 自摸相关
  | 'special' // 特殊情况（海底/岭上等）
  | 'shape' // 牌型役
  | 'suit' // 花色役
  | 'honor' // 字牌役
  | 'yakuman'; // 役满

/** 役种等级 */
export type YakuLevel = 'normal' | 'yakuman' | 'double-yakuman';

/** 役种定义 */
export interface YakuDef {
  /** 役种 ID（英文小写） */
  readonly id: string;
  /** 中文名 */
  readonly name: string;
  /** 日文名（罗马字，用于国际化） */
  readonly romaji: string;
  /** 番数（役满用 13 表示） */
  readonly han: number;
  /** 类别 */
  readonly category: YakuCategory;
  /** 等级 */
  readonly level: YakuLevel;
  /** 是否必须门清（副露后失效） */
  readonly closedOnly: boolean;
  /** 副露后的番数（不设置则与 han 相同；设为 0 表示副露后不成立） */
  readonly openHan?: number;
  /** 说明 */
  readonly description?: string;
}

/** 判定出的役（含实际番数） */
export interface Yaku {
  readonly def: YakuDef;
  /** 实际番数（考虑副露降番） */
  readonly han: number;
}

// ════════════════════════════════════════════════════════════════
// 场况上下文
// ════════════════════════════════════════════════════════════════

/** 风 */
export type Wind = 'east' | 'south' | 'west' | 'north';

/** 和牌方式 */
export type WinMethod = 'tsumo' | 'ron';

/** 宝牌指示牌信息 */
export interface DoraInfo {
  /** 宝牌指示牌（正面） */
  readonly doraIndicators: readonly number[];
  /** 里宝牌指示牌（立直后有效） */
  readonly uraIndicators?: readonly number[];
  /** 赤宝牌数量 */
  readonly redFives?: number;
}

/** 和牌上下文（场况） */
export interface WinContext {
  /** 场风 */
  readonly roundWind: Wind;
  /** 自风 */
  readonly seatWind: Wind;
  /** 和牌方式 */
  readonly winMethod: WinMethod;
  /** 是否立直 */
  readonly isRiichi?: boolean;
  /** 是否两立直（第一巡立直） */
  readonly isDoubleRiichi?: boolean;
  /** 是否一发 */
  readonly isIppatsu?: boolean;
  /** 是否海底摸月（自摸最后一张） */
  readonly isHaitei?: boolean;
  /** 是否河底摸鱼（荣和最后一张弃牌） */
  readonly isHoutei?: boolean;
  /** 是否岭上开花 */
  readonly isRinshan?: boolean;
  /** 是否抢杠 */
  readonly isChankan?: boolean;
  /** 是否天和（庄家配牌即和） */
  readonly isTenhou?: boolean;
  /** 是否地和（子家第一巡自摸） */
  readonly isChiihou?: boolean;
  /** 宝牌信息 */
  readonly dora?: DoraInfo;
  /** 和了牌（牌索引）—— 用于符数计算和特殊役判定 */
  readonly winTile?: number;
  /** 本场数 */
  readonly honba?: number;
  /**
   * 待ち读法覆盖（高点法用）——
   * 同一分解下和了牌既可补顺子（两面）又可为雀头（单骑）时，分别求值取点数高者。
   * 'tanki' = 强制单骑（不计平和、+2 符）；'ryanmen' = 强制两面。
   */
  readonly waitReading?: 'tanki' | 'ryanmen' | 'kanchan' | 'penchan';
}

// ════════════════════════════════════════════════════════════════
// 判定输入
// ════════════════════════════════════════════════════════════════

/** 役种判定输入 */
export interface YakuContext {
  /** 和牌分解（面子 + 雀头） */
  readonly decomposition: Decomposition;
  /** 手牌计数（不含副露，14 张或含和了牌） */
  readonly counts: Counts;
  /** 全部牌（含副露的牌，用于牌型役判定） */
  readonly allTiles: Counts;
  /** 副露面子数 */
  readonly meldCount: number;
  /** 是否门清（无副露） */
  readonly isClosed: boolean;
  /** 和了牌索引 */
  readonly winTile: number;
  /** 场况 */
  readonly context: WinContext;
}

/** 役种判定结果 */
export interface YakuResult {
  /** 命中的役种列表 */
  readonly yaku: readonly Yaku[];
  /** 总番数（役种番数之和，不含宝牌） */
  readonly han: number;
  /** 宝牌番数（宝牌 + 里宝牌 + 赤宝牌） */
  readonly doraHan: number;
  /** 总番数（含宝牌） */
  readonly totalHan: number;
  /** 是否包含役满 */
  readonly hasYakuman: boolean;
  /** 役满数量（累计倍数） */
  readonly yakumanCount: number;
  /** 是否无役（不允许和牌） */
  readonly isYakuless: boolean;
}

// ════════════════════════════════════════════════════════════════
// 辅助
// ════════════════════════════════════════════════════════════════

/** 创建役种定义 */
export function defineYaku(def: YakuDef): YakuDef {
  return def;
}

/** 计算实际番数（考虑副露降番） */
export function effectiveHan(def: YakuDef, isClosed: boolean): number {
  if (isClosed) return def.han;
  if (def.closedOnly) return 0;
  return def.openHan ?? def.han;
}

/** 创建命中役 */
export function makeYaku(def: YakuDef, isClosed: boolean): Yaku {
  return { def, han: effectiveHan(def, isClosed) };
}

/** 面子是否含指定牌 */
export function mentsuHas(m: Mentsu, tile: number): boolean {
  if (m.kind === 'shuntsu') {
    return tile >= m.start && tile <= m.start + 2;
  }
  return m.start === tile;
}
